import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, Check, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const dropdownVariants = {
  hidden: { opacity: 0, y: -10, scaleY: 0.95, originY: 0 },
  visible: {
    opacity: 1,
    y: 0,
    scaleY: 1,
    originY: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -5,
    scaleY: 0.95,
    originY: 0,
    transition: { duration: 0.15, ease: "easeIn" },
  },
};

const CustomSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select",
  required = false,
  isClearable = true,
  error = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option, e) => {
    e.stopPropagation();
    onChange(option);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Prevent event propagation for all clicks inside the dropdown
  useEffect(() => {
    const handleDropdownClick = (e) => {
      e.stopPropagation();
    };

    const dropdown = dropdownRef.current;
    if (dropdown) {
      dropdown.addEventListener("mousedown", handleDropdownClick);
      return () => {
        dropdown.removeEventListener("mousedown", handleDropdownClick);
      };
    }
  }, []);

  return (
    <div className="mt-1" ref={dropdownRef}>
      {label && (
        <label className="text-sm font-medium text-black mb-2 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative mb-3">
        <button
          onClick={toggleDropdown}
          className={`flex items-center justify-between w-full h-10 px-3 text-sm bg-white border rounded-lg hover:border-gray-400 focus:outline-none ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        >
          <div className="flex items-center gap-2 font-medium text-black">
            <span>
              {value?.label || (
                <span className="text-grey font-medium">{placeholder}</span>
              )}
            </span>
            {value && isClearable && (
              <X
                size={16}
                className="text-black mt-1 cursor-pointer"
                onClick={handleClear}
              />
            )}
          </div>
          {isOpen ? (
            <ChevronUp size={18} className="text-black" />
          ) : (
            <ChevronDown size={18} className="text-black" />
          )}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={dropdownVariants}
              className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto origin-top"
            >
              <ul className="py-1">
                {options.map((option) => (
                  <li
                    key={option.value}
                    onClick={(e) => handleOptionClick(option, e)}
                    className={`flex items-center justify-between font-normal px-3 py-2 text-sm cursor-pointer ${
                      value?.value === option.value
                        ? "bg-[#4B00E0] text-white"
                        : "hover:bg-gray-100 text-black"
                    }`}
                  >
                    <span>{option.label}</span>
                    {value?.value === option.value && <Check size={16} />}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && <p className="text-red font-semibold text-sm mt-1">{error}</p>}
    </div>
  );
};

export default CustomSelect;
